/** WhatsApp Cloud API limits for interactive list messages */
export const MAX_LIST_ROWS = 10;
export const LIST_BUTTON_MAX = 20;
export const ROW_TITLE_MAX = 24;
export const ROW_DESC_MAX = 72;
export const SECTION_TITLE_MAX = 24;
export const BUTTON_TITLE_MAX = 20;
export const BODY_MAX = 1024;

export const EMPTY_RULE = { add_tags: [], priority: '', suggest_activity: '' };

export const EMPTY_INTERACTIVE_PRESET_FORM = {
  label: '',
  body: '',
  preset_type: 'button',
  buttons: [{ id: 'btn_1', title: '', rule: null }],
  listButton: 'Menu',
  sections: [{ title: '', rows: [{ id: 'row_1', title: '', description: '', rule: null }] }],
};

/** Extract and normalise a rule from a button or list row object */
export function extractRuleFromButton(btn) {
  if (!btn || !btn.rule || typeof btn.rule !== 'object') return null;
  const r = btn.rule;
  return {
    add_tags: Array.isArray(r.add_tags) ? r.add_tags.map((t) => String(t).trim().toLowerCase()).filter(Boolean) : [],
    priority: ['low', 'medium', 'high'].includes(r.priority) ? r.priority : '',
    suggest_activity: r.suggest_activity ? String(r.suggest_activity).trim().slice(0, 200) : '',
  };
}

/** Returns true if the rule has at least one non-empty action */
export function ruleIsActive(rule) {
  if (!rule) return false;
  return (rule.add_tags && rule.add_tags.length > 0) ||
    Boolean(rule.priority) ||
    Boolean(rule.suggest_activity);
}

/**
 * @param {unknown} raw — JSONB from API (object or string)
 * @param {'button'|'list'} presetType
 */
export function parseStoredButtons(raw, presetType) {
  let parsed = raw;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      parsed = presetType === 'list' ? {} : [];
    }
  }
  if ((presetType || 'button') === 'list') {
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return {
        mode: 'list',
        listButton: String(parsed.listButton || 'Menu').slice(0, LIST_BUTTON_MAX),
        sections: Array.isArray(parsed.sections) ? parsed.sections : [],
      };
    }
    return { mode: 'list', listButton: 'Menu', sections: [] };
  }
  const arr = Array.isArray(parsed) ? parsed : [];
  return { mode: 'button', buttons: arr };
}

/** DB row → form state */
export function presetRecordToForm(p) {
  const pt = p.preset_type || 'button';
  const parsed = parseStoredButtons(p.buttons, pt);
  if (parsed.mode === 'list') {
    const sections =
      parsed.sections && parsed.sections.length
        ? parsed.sections.map((s, si) => ({
            title: s.title || '',
            rows: (Array.isArray(s.rows) ? s.rows : []).map((r, ri) => ({
              id: r.id || `row_${si + 1}_${ri + 1}`,
              title: r.title || '',
              description: r.description || '',
              rule: extractRuleFromButton(r),
            })),
          }))
        : [{ title: '', rows: [{ id: 'row_1', title: '', description: '', rule: null }] }];
    return {
      label: p.label,
      body: p.body,
      preset_type: 'list',
      listButton: parsed.listButton || 'Menu',
      sections,
      buttons: [{ id: 'btn_1', title: '', rule: null }],
    };
  }
  const btns = parsed.buttons || [];
  return {
    label: p.label,
    body: p.body,
    preset_type: 'button',
    listButton: 'Menu',
    sections: [{ title: '', rows: [{ id: 'row_1', title: '', description: '', rule: null }] }],
    buttons: btns.length
      ? btns.map((b, i) => ({ id: b.id || `btn_${i + 1}`, title: b.title || '', rule: extractRuleFromButton(b) }))
      : [{ id: 'btn_1', title: '', rule: null }],
  };
}

/** Normalise a rule for storage — strips empty fields, returns null if inactive */
function normalizeRuleForStorage(rule) {
  if (!rule || typeof rule !== 'object') return null;
  const tags = Array.isArray(rule.add_tags) ? rule.add_tags.map((t) => String(t).trim().toLowerCase()).filter(Boolean) : [];
  const priority = ['low', 'medium', 'high'].includes(rule.priority) ? rule.priority : null;
  const suggest = rule.suggest_activity ? String(rule.suggest_activity).trim().slice(0, 200) : null;
  if (!tags.length && !priority && !suggest) return null;
  const out = {};
  if (tags.length) out.add_tags = tags;
  if (priority) out.priority = priority;
  if (suggest) out.suggest_activity = suggest;
  return out;
}

/** Form → value stored in `buttons` JSONB */
export function formToStoredButtons(presetForm) {
  if (presetForm.preset_type === 'list') {
    const lb = String(presetForm.listButton || '').trim().slice(0, LIST_BUTTON_MAX) || 'Menu';
    const sections = (presetForm.sections || []).map((s) => ({
      title: String(s.title || '').trim().slice(0, SECTION_TITLE_MAX),
      rows: (s.rows || []).map((r, i) => {
        const row = {
          id: String(r.id || '').trim().slice(0, 200) || `row_${i + 1}`,
          title: String(r.title || '').trim().slice(0, ROW_TITLE_MAX),
          description: String(r.description || '').trim().slice(0, ROW_DESC_MAX),
        };
        const rule = normalizeRuleForStorage(r.rule);
        if (rule) row.rule = rule;
        return row;
      }),
    }));
    return { listButton: lb, sections };
  }
  return (presetForm.buttons || [])
    .filter((b) => (b.title || '').trim())
    .map((b, i) => {
      const btn = {
        id: String(b.id || `btn_${i + 1}`).trim().slice(0, 200),
        title: String(b.title || '').trim().slice(0, BUTTON_TITLE_MAX),
      };
      const rule = normalizeRuleForStorage(b.rule);
      if (rule) btn.rule = rule;
      return btn;
    });
}

/** Preset record → Meta `interactive` object for send */
export function buildInteractiveFromPreset(preset) {
  const pt = preset.preset_type || 'button';
  const bodyText = String(preset.body || '').trim().slice(0, BODY_MAX);
  if (pt === 'list') {
    const data = parseStoredButtons(preset.buttons, 'list');
    const btnLabel = String(data.listButton || 'Menu').trim().slice(0, LIST_BUTTON_MAX) || 'Menu';
    const sectionsOut = [];
    let totalRows = 0;
    for (const s of data.sections || []) {
      const rows = [];
      for (const r of s.rows || []) {
        if (totalRows >= MAX_LIST_ROWS) break;
        const title = String(r.title || '').trim();
        if (!title) continue;
        totalRows += 1;
        const id = String(r.id || '').trim().slice(0, 200) || `row_${totalRows}`;
        const row = { id, title: title.slice(0, ROW_TITLE_MAX) };
        const desc = String(r.description || '').trim();
        if (desc) row.description = desc.slice(0, ROW_DESC_MAX);
        rows.push(row);
      }
      if (rows.length) {
        const sec = { rows };
        const st = String(s.title || '').trim();
        if (st) sec.title = st.slice(0, SECTION_TITLE_MAX);
        sectionsOut.push(sec);
      }
      if (totalRows >= MAX_LIST_ROWS) break;
    }
    return {
      type: 'list',
      body: { text: bodyText },
      action: { button: btnLabel, sections: sectionsOut },
    };
  }
  const { buttons } = parseStoredButtons(preset.buttons, 'button');
  const valid = (buttons || []).filter((b) => String(b.title || '').trim());
  return {
    type: 'button',
    body: { text: bodyText },
    action: {
      buttons: valid.map((b, i) => ({
        type: 'reply',
        reply: {
          id: String(b.id || `btn_${i + 1}`).slice(0, 200),
          title: String(b.title || '').trim().slice(0, BUTTON_TITLE_MAX),
        },
      })),
    },
  };
}

export function countListRows(sections) {
  return (sections || []).reduce((n, s) => n + (Array.isArray(s.rows) ? s.rows.filter((r) => String(r.title || '').trim()).length : 0), 0);
}
